import { action } from '@ember/object';
import ObjectProxy from '@ember/object/proxy';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import PowerSelect from 'ember-power-select/components/power-select';
import { or } from 'ember-truth-helpers';
import type Printer from 'garaje/models/printer';

class PrinterOption extends ObjectProxy<Printer> {
  /**
   * Set *only* via `PrinterOption.create({ disabledOverride })`. ProxyMixin's
   * `setUnknownProperty` defines the key on the proxy during
   * `meta.isInitializing()`; any later `option.set('disabledOverride', …)`
   * would be delegated to the underlying `Printer` model and dirty it.
   * Treat as effectively readonly post-construction.
   */
  declare disabledOverride?: boolean;

  get name() {
    const name = this.content?.name;
    if (this.disabled) {
      return `${name} (Printer offline)`;
    }
    return name;
  }
  get disabled() {
    return this.disabledOverride ?? this.content?.status !== 'connected';
  }

  get id() {
    return this.content?.id;
  }
}

interface PrinterSelectorSignature {
  Args: {
    selectedPrinter?: Printer;
    printers: Printer[];
    placeholder?: string;
    onChange: (printer?: Printer) => void;
    /**
     * Optional: override the default `status !== 'connected'` rule for determining
     * whether a printer option is disabled in the dropdown.
     */
    isDisabled?: (printer: Printer) => boolean;
  };
  Element: HTMLDivElement;
}

export default class PrinterSelector extends Component<PrinterSelectorSignature> {
  @tracked selectedPrinter;

  constructor(owner: unknown, args: PrinterSelectorSignature['Args']) {
    super(owner, args);
    const { printerOptions } = this;
    this.selectedPrinter = printerOptions.find((option) => option.id === this.args.selectedPrinter?.id);
  }

  get printerOptions(): PrinterOption[] {
    const { printers, isDisabled } = this.args;
    return printers
      .toSorted((a, b) => a.name.localeCompare(b.name))
      .map((printer) =>
        PrinterOption.create({
          content: printer,
          disabledOverride: isDisabled ? isDisabled(printer) : undefined,
        }),
      );
  }

  @action
  selectPrinter(printer: PrinterOption): void {
    this.selectedPrinter = printer;
    this.args.onChange(printer?.content);
  }

  <template>
    <PowerSelect
      @dropdownClass="in-modal"
      @options={{this.printerOptions}}
      @selected={{this.selectedPrinter}}
      @placeholder={{or @placeholder "Choose a printer"}}
      @searchEnabled={{false}}
      @onChange={{this.selectPrinter}}
      @allowClear={{true}}
      as |printer|
    >
      {{printer.name}}
    </PowerSelect>
  </template>
}
